import React, { useCallback, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { reportsAPI } from '../../services/api';
import useReport from './shared/useReport';
import ChartPanel from './shared/ChartPanel';
import ReportTable from './shared/ReportTable';
import ReportPrintLayout from './shared/ReportPrintLayout';
import PrintButton from './shared/PrintButton';
import DateRangeFilter, { getPresetRange } from './shared/DateRangeFilter';
import { axisProps, tooltipStyle, barColor } from './shared/chartTheme';
import useDrill from './shared/useDrill';

// Which lab tests the doctors order most, how often they are urgent, and how many have a report
// attached. "Completed" means a report document was linked - results as values are not captured
// by the app yet.

const COLUMNS = [
  { key: 'test', label: 'Lab Test' },
  { key: 'orders', label: 'Orders', align: 'right' },
  { key: 'urgent', label: 'Urgent', align: 'right', render: (r) => (Number(r.urgent) > 0 ? <span className="text-amber-600 font-semibold">{r.urgent}</span> : '—'), csv: (r) => r.urgent },
  { key: 'uniquePatients', label: 'Unique Patients', align: 'right' },
  { key: 'completed', label: 'With Report', align: 'right' },
];

const TopLabTestsReport = () => {
  const drill = useDrill();
  const [range, setRange] = useState(() => getPresetRange('thisMonth'));
  const [preset, setPreset] = useState('thisMonth');

  const fetcher = useCallback(
    () => reportsAPI.getTopLabTests({ from: range.from, to: range.to, limit: 30 }),
    [range]
  );
  const { data, loading } = useReport(fetcher);

  const top15 = (data?.rows || []).slice(0, 15).map((r) => ({ ...r, orders: Number(r.orders) }));

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[300px]">
          <DateRangeFilter value={range} preset={preset} onChange={(r, p) => { setRange(r); setPreset(p); }} />
        </div>
        <div className="pt-4"><PrintButton /></div>
      </div>

      <ChartPanel title="Top 15 ordered lab tests">
        {loading || top15.length === 0 ? (
          <p className="text-gray-400 text-sm text-center py-16">
            {loading ? 'Loading' : 'No lab tests ordered in the selected range'}
          </p>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(220, top15.length * 30)}>
            <BarChart data={top15} layout="vertical" margin={{ left: 8, right: 16 }}>
              <CartesianGrid stroke="#e5e7eb" strokeDasharray="2 3" horizontal={false} />
              <XAxis type="number" {...axisProps} allowDecimals={false} />
              <YAxis type="category" dataKey="test" {...axisProps} width={180} />
              <Tooltip {...tooltipStyle} />
              <Bar dataKey="orders" name="Orders" radius={[0, 4, 4, 0]} barSize={16}>
              {top15.map((_, i) => <Cell key={i} fill={barColor(i)} />)}
            </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartPanel>

      <ReportTable
        onRowClick={(r) => drill.open({ type: 'labTest', value: r.test, from: range.from, to: range.to })}
        title="Ordered lab tests"
        columns={COLUMNS}
        rows={data?.rows || []}
        csvName={`top-lab-tests-${range.from}-to-${range.to}`}
      />

      <ReportPrintLayout
        title="Top Lab Tests"
        subtitle={`Period: ${range.from} to ${range.to}`}
        tables={[{ title: 'Ordered lab tests', columns: COLUMNS, rows: data?.rows || [] }]}
      />

      {drill.node}
    </div>
  );
};

export default TopLabTestsReport;
